import type { Client } from '@openfeature/web-sdk';
import { OpenFeature, ProviderEvents } from '@openfeature/web-sdk';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { featureFlagDefinitions } from '@risksmart-app/feature-flags/src/flags';
import type { FeatureFlag, OrgFeature } from '@risksmart-app/modules/src/index';
import { useCallback, useContext, useEffect, useReducer } from 'react';
import { E2eFlagOverridesContext } from 'src/rbac/E2eFlagOverridesContext';

// The global web-sdk client rather than the react-sdk context client:
// flag-gated components render in hundreds of test trees (and error paths)
// with no <OpenFeatureProvider> mounted, where the context hooks throw
// MissingContextError. The global client never throws — with no provider
// registered it evaluates registry defaults via the no-op provider.
const client: Client = OpenFeature.getClient();

// e2e test orgs (non-null overrides) resolve from the seeded Meta.features
// list rather than the provider — see E2eFlagOverridesContext for why. The
// REACT_APP_FEATURE_* force-enable applies on both branches.
const evaluate = (
  flag: FeatureFlag,
  e2eFlagOverrides: OrgFeature[] | null
): boolean =>
  (e2eFlagOverrides
    ? e2eFlagOverrides.includes(flag)
    : client.getBooleanValue(
        flag,
        featureFlagDefinitions[flag].defaultValue
      )) || getEnv(`REACT_APP_FEATURE_${flag}`, true) === 'true';

/**
 * Re-renders the consumer whenever flag values can have changed — provider
 * readiness, configuration updates, or context changes — so values read
 * during render stay current.
 */
const useFlagChangeSubscription = (): void => {
  const [, bumpRevision] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const events = [
      ProviderEvents.Ready,
      ProviderEvents.ConfigurationChanged,
      ProviderEvents.ContextChanged,
    ];
    const handler = (): void => {
      bumpRevision();
    };
    events.forEach((event) => client.addHandler(event, handler));

    return () => {
      events.forEach((event) => client.removeHandler(event, handler));
    };
  }, []);
};

/**
 * Hook to check if a feature flag is enabled. Evaluated via OpenFeature
 * against the globally registered provider (`initialiseWebFeatureFlags` at
 * boot), falling back to the registry default. The `REACT_APP_FEATURE_*` env
 * var force-ENABLES a flag for local development (it cannot force-disable).
 *
 * For e2e test organisations (`Meta.isE2eTestOrg`) the org's seeded
 * `Meta.features` list is authoritative instead — flags a regression spec
 * seeds via `configureOrganisation` must be read through this hook (or its
 * lazy variant); direct `useBooleanFlagValue` reads bypass the override.
 */
export const useIsFeatureFlagEnabled = (flag: FeatureFlag): boolean => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();

  return evaluate(flag, e2eFlagOverrides);
};

/**
 * Lazy variant that returns a callback for checking feature flags. Useful when
 * you need to check multiple flags or pass the check as a callback. The
 * callback reads the live flag value on each invocation.
 */
export const useIsFeatureFlagEnabledLazy = (): ((
  flag: FeatureFlag
) => boolean) => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();

  return useCallback(
    (flag: FeatureFlag) => evaluate(flag, e2eFlagOverrides),
    [e2eFlagOverrides]
  );
};
